'use client';

import { useState } from 'react';
import { AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ACTION_STATE_COPY, diffArguments, effectiveStatus } from '@/lib/assistant/actions';
import { usePermissions } from '@/lib/hooks/use-permissions';
import type { AgentPendingAction } from '@/lib/api/types';

export type ResolveKind = 'apply' | 'cancel' | 'applyAll';

/**
 * The confirmation gate.
 *
 * Every non-read tool call arrives here — there is no allow-list of "safe"
 * writes on the backend, so there is none here either. Apply re-checks the
 * caller's role, the 24-hour expiry and the before-snapshot hash, which is why
 * a single button can come back applied, failed, stale or expired, and all
 * five settled states are rendered rather than collapsed into success and
 * failure. Apply-all can stop partway through, so a card with some actions
 * applied and others still pending is a normal, correct state — not a bug to
 * normalise away.
 */
export function ConfirmationCard({
  actions,
  onResolve,
  busy = false,
}: {
  actions: AgentPendingAction[];
  onResolve: (kind: ResolveKind, id: string) => void;
  busy?: boolean;
}) {
  const [confirming, setConfirming] = useState<AgentPendingAction | null>(null);
  const { can, reasonFor } = usePermissions();
  // Applying anything needs at least editor; the backend re-checks the tool's
  // own minimum role on apply, so this only saves a round trip.
  const allowed = can('editor');
  const reason = reasonFor('editor');
  const pending = actions.filter((one) => effectiveStatus(one) === 'pending');

  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
      {!allowed && pending.length > 0 && (
        <p className="mb-2 text-xs text-muted-foreground">{reason}</p>
      )}

      {pending.length > 1 && (
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium">{pending.length} changes proposed</span>
          <Button
            size="sm"
            variant="outline"
            disabled={busy || !allowed}
            // Apply-all is conversation-scoped, not tied to any one action —
            // `resolve('applyAll', id)` in assistant-provider ignores id
            // entirely. Pass '' rather than `pending[0].id`, which read as if
            // the first pending action were special.
            onClick={() => onResolve('applyAll', '')}
          >
            Apply all
          </Button>
        </div>
      )}

      <ul className="flex flex-col gap-3">
        {actions.map((action) => {
          const status = effectiveStatus(action);
          const changes = diffArguments(action.before, action.arguments);

          return (
            <li key={action.id} className="text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium">{action.title}</span>
                {action.destructive && (
                  <Badge variant="destructive" className="gap-1">
                    <AlertTriangle className="size-3" />
                    Destructive
                  </Badge>
                )}
              </div>

              <dl className="mt-1.5 flex flex-col gap-0.5 text-xs">
                {changes.map((change) => (
                  <div key={change.key} className="flex flex-wrap items-center gap-1.5">
                    <dt className="text-muted-foreground">{change.key}</dt>
                    <dd className="flex items-center gap-1.5">
                      {change.from !== null && (
                        <>
                          <span className="text-muted-foreground line-through">{change.from}</span>
                          <ArrowRight className="size-3 text-muted-foreground" />
                        </>
                      )}
                      <span className="font-mono">{change.to}</span>
                    </dd>
                  </div>
                ))}
              </dl>

              {status === 'pending' ? (
                <div className="mt-2 flex items-center gap-2">
                  <Button
                    size="sm"
                    disabled={busy || !allowed}
                    title={allowed ? undefined : reason}
                    onClick={() => (action.destructive ? setConfirming(action) : onResolve('apply', action.id))}
                  >
                    {busy && <Loader2 className="mr-1.5 size-3 animate-spin" />}
                    Apply
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => onResolve('cancel', action.id)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">{ACTION_STATE_COPY[status]}</p>
              )}
            </li>
          );
        })}
      </ul>

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirming?.title}</AlertDialogTitle>
            <AlertDialogDescription>Are you sure? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            {/* The full action title, not a hardcoded "Delete": there is no
                allow-list of destructive tools on the backend, so a suspend
                or a role downgrade needs its own label, not a lie. The title
                is already shown as the dialog's heading, so repeating it here
                loses nothing — and unlike a first-word-of-title heuristic, it
                never degrades a non-imperative title to a bare noun. */}
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => {
                const target = confirming;
                setConfirming(null);
                if (target) onResolve('apply', target.id);
              }}
            >
              {confirming?.title ?? 'Confirm'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
